import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ArrowSync16Regular } from "@fluentui/react-icons";
import {
  Field,
  Combobox,
  Option,
  SelectionEvents,
  OptionOnSelectData,
  Button,
  OptionGroup,
  Tooltip,
} from "@fluentui/react-components";

import { Stack } from "@/components/stack";
import { YellowPadLogo } from "@/components/yellowpad-logo";
import { useRiskAnalysisStyles } from "@/pages/risk-analysis/styles";

import { Playbook } from "@/types/risk-analysis";

import { useAuth } from "@/context/auth";
import { useDocumentClassification } from "@/context/document-classification";

export const PlaybookSection = () => {
  const styles = useRiskAnalysisStyles();

  const {
    errors,
    playbooks,
    fetchPlaybooks,
    fetchingPlaybooks,
    selectedPlaybook,
    setSelectedPlaybook,
    currentClassification,
  } = useDocumentClassification();
  const { user: auth } = useAuth();

  const [value, setValue] = useState<string>("");
  const myPlaybooks = useMemo(() => playbooks.filter((playbook) => playbook.customerId), [playbooks]);
  const yellowpadPlaybooks = useMemo(() => playbooks.filter((playbook) => !playbook.customerId), [playbooks]);

  const renderName = useCallback(
    (playbook?: Playbook) => {
      if (!playbook || playbooks.length === 0) return "";
      return [playbook.name, playbook.id === playbooks[0].id && "(Default)"].filter(Boolean).join(" ");
    },
    [playbooks]
  );

  const handlePlaybookChange = useCallback(
    async (_: SelectionEvents, data: OptionOnSelectData) => {
      const selected = playbooks.find((playbook) => playbook.id === data.optionValue);
      if (!selected) return;
      setSelectedPlaybook(selected);
    },
    [playbooks, setSelectedPlaybook]
  );

  useEffect(() => {
    if (selectedPlaybook) {
      setValue(renderName(selectedPlaybook));
    }
  }, [setValue, renderName, selectedPlaybook]);

  const isYellowpadPlaybook = !!selectedPlaybook && !selectedPlaybook.customerId;
  const disabled = fetchingPlaybooks || currentClassification?.rules.some((rule) => rule.isLoading);
  return (
    <div className={styles.configContainer}>
      <div className={styles.configInnerContainer}>
        <Field
          label="Playbook"
          validationState={errors.find((error) => error.field === "playbook") ? "error" : "none"}
          validationMessage={errors.find((error) => error.field === "playbook")?.message || ""}
          data-lpignore="true"
        >
          <div className={styles.configButtonContainer}>
            <div style={{ position: "relative" }}>
              <Combobox
                disabled={disabled}
                data-lpignore="true"
                id="playbook-config-combobox"
                placeholder="Select playbook"
                aria-labelledby="playbook-config-label"
                value={value}
                selectedOptions={[selectedPlaybook?.id]}
                input={{ style: { paddingLeft: isYellowpadPlaybook ? "2rem" : "0.75rem" } }}
                onChange={(e) => setValue(e.target.value)}
                onOptionSelect={handlePlaybookChange}
              >
                <OptionGroup label={auth.user.customer?.name || auth.user.name}>
                  {myPlaybooks.length === 0 && (
                    <Option disabled value="">
                      No options available
                    </Option>
                  )}
                  {myPlaybooks.length > 0 &&
                    myPlaybooks.map((playbook) => (
                      <Option key={playbook.id} value={playbook.id}>
                        {renderName(playbook)}
                      </Option>
                    ))}
                </OptionGroup>
                <OptionGroup
                  label={
                    <Stack align="center" gap="0.25rem">
                      <YellowPadLogo width={16} height={16} />
                      <span>Yellowpad</span>
                    </Stack>
                  }
                >
                  {yellowpadPlaybooks.length === 0 && (
                    <Option disabled value="">
                      No options available
                    </Option>
                  )}
                  {yellowpadPlaybooks.length > 0 &&
                    yellowpadPlaybooks.map((playbook) => (
                      <Option key={playbook.id} value={playbook.id}>
                        {renderName(playbook)}
                      </Option>
                    ))}
                </OptionGroup>
              </Combobox>
              {isYellowpadPlaybook && (
                <Tooltip relationship="label" content="The selected playbook is from Yellowpad">
                  <YellowPadLogo width={16} height={16} className={styles.logo} />
                </Tooltip>
              )}
            </div>
            <Button
              disabled={disabled}
              icon={<ArrowSync16Regular />}
              aria-label="Refresh playbooks"
              onClick={fetchPlaybooks}
            />
          </div>
        </Field>
      </div>
    </div>
  );
};
